import { Mat4, Quat, Vec3 } from 'playcanvas';

import { PlacePivotOp, EntityTransformOp, MultiOp } from './edit-ops';
import { Events } from './events';
import { Pivot } from './pivot';
import { Splat } from './splat';
import { Transform } from './transform';
import { TransformHandler } from './transform-handler';

const mat = new Mat4();
const quat = new Quat();
const transform = new Transform();

// work globals for multi-object transforms
const deltaPos = new Vec3();
const deltaScale = new Vec3();
const deltaRot = new Quat();
const invStartRot = new Quat();
const tmpPos = new Vec3();
const tmpRot = new Quat();
const tmpScale = new Vec3();

// per-element transform target (an element + its world transform at drag start)
type Target = {
    element: any;
    startPos: Vec3;
    startRot: Quat;
    startScale: Vec3;
    op: EntityTransformOp;
};

class EntityTransformHandler implements TransformHandler {
    events: Events;
    splat: Splat;          // primary selection (single-selection path)
    targets: Target[] = []; // multi-selection drag targets
    pivotStart = new Transform();
    top: EntityTransformOp;
    pop: PlacePivotOp;
    bindMat = new Mat4();

    constructor(events: Events) {
        this.events = events;

        events.on('pivot.started', (pivot: Pivot) => {
            if (this.active) {
                this.start();
            }
        });

        events.on('pivot.moved', (pivot: Pivot) => {
            if (this.active) {
                this.update(pivot.transform);
            }
        });

        events.on('pivot.ended', (pivot: Pivot) => {
            if (this.active) {
                this.end();
            }
        });

        events.on('pivot.origin', (mode: 'center' | 'boundCenter') => {
            if (this.active) {
                this.placePivot();
            }
        });

        events.on('camera.focalPointPicked', (details: { splat: Splat, position: Vec3 }) => {
            if (this.active && ['move', 'rotate', 'scale'].includes(this.events.invoke('tool.active'))) {
                const pivot = events.invoke('pivot') as Pivot;
                const newt = new Transform(details.position, pivot.transform.rotation, pivot.transform.scale);
                const op = new PlacePivotOp({ pivot, oldt: pivot.transform.clone(), newt });
                events.fire('edit.add', op);
            }
        });
    }

    private get selection(): any[] {
        return (this.events.invoke('selection.all') as any[]) ?? [];
    }

    private get isMulti(): boolean {
        return this.selection.length > 1;
    }

    private get active(): boolean {
        return this.isMulti ? this.selection.length > 0 : !!this.splat;
    }

    placePivot() {
        const selection = this.selection;
        if (selection.length > 1) {
            // place the pivot at the centroid of the selected entities
            tmpPos.set(0, 0, 0);
            selection.forEach((el) => {
                tmpPos.add(el.entity.getPosition());
            });
            tmpPos.mulScalar(1 / selection.length);
            transform.set(tmpPos, Quat.IDENTITY, Vec3.ONE);
            this.events.invoke('pivot').place(transform);
            return;
        }

        // single-selection: place pivot on the element itself
        const origin = this.events.invoke('pivot.origin');
        this.splat.getPivot(origin === 'center' ? 'center' : 'boundCenter', false, transform);
        this.events.invoke('pivot').place(transform);
    }

    activate() {
        const selection = this.selection;
        this.splat = (this.events.invoke('selection') as Splat) ?? null;
        if (selection.length > 0) {
            this.placePivot();
        }
    }

    deactivate() {
        this.splat = null;
        this.targets = [];
    }

    start() {
        const pivot = this.events.invoke('pivot') as Pivot;
        const { transform } = pivot;

        if (this.isMulti) {
            // capture each element's starting world transform; apply the gizmo's
            // delta to each in its own world frame (own-origin scale/rotate)
            this.pivotStart.copy(transform);
            this.targets = this.selection.map((element) => {
                const p = new Vec3();
                const r = new Quat();
                const s = new Vec3();
                element.getWorldTransformComponents(p, r, s);
                // The op stores LOCAL values, so undo and the .ssproj format are unchanged;
                // only the gizmo maths below works in world space.
                const lp = element.entity.getLocalPosition().clone();
                const lr = element.entity.getLocalRotation().clone();
                const ls = element.entity.getLocalScale().clone();
                return {
                    element,
                    startPos: p,
                    startRot: r,
                    startScale: s,
                    op: new EntityTransformOp({
                        splat: element,
                        oldt: new Transform(lp, lr, ls),
                        newt: new Transform(lp.clone(), lr.clone(), ls.clone())
                    })
                };
            });
            this.pop = new PlacePivotOp({
                pivot,
                oldt: transform.clone(),
                newt: transform.clone()
            });
            return;
        }

        // single-selection path (rigid follow of the gizmo)
        const { entity } = this.splat;
        this.bindMat.setTRS(transform.position, transform.rotation, transform.scale);
        this.bindMat.invert();
        this.bindMat.mul2(this.bindMat, entity.getWorldTransform());

        const p = entity.getLocalPosition();
        const r = entity.getLocalRotation();
        const s = entity.getLocalScale();

        this.top = new EntityTransformOp({
            splat: this.splat,
            oldt: new Transform(p, r, s),
            newt: new Transform(p, r, s)
        });

        this.pop = new PlacePivotOp({
            pivot,
            oldt: transform.clone(),
            newt: transform.clone()
        });
    }

    update(transform: Transform) {
        if (this.isMulti) {
            // decompose the pivot delta and apply each component in each element's local frame
            deltaPos.sub2(transform.position, this.pivotStart.position);
            deltaScale.set(
                transform.scale.x / this.pivotStart.scale.x,
                transform.scale.y / this.pivotStart.scale.y,
                transform.scale.z / this.pivotStart.scale.z
            );
            invStartRot.copy(this.pivotStart.rotation).invert();
            deltaRot.mul2(transform.rotation, invStartRot);

            this.targets.forEach((t) => {
                tmpPos.add2(t.startPos, deltaPos);
                tmpScale.set(
                    t.startScale.x * deltaScale.x,
                    t.startScale.y * deltaScale.y,
                    t.startScale.z * deltaScale.z
                );
                tmpRot.mul2(deltaRot, t.startRot);

                t.element.moveWorld(tmpPos, tmpRot, tmpScale);
                t.op.newt.set(
                    t.element.entity.getLocalPosition(),
                    t.element.entity.getLocalRotation(),
                    t.element.entity.getLocalScale()
                );
            });

            this.pop.newt.copy(transform);
            return;
        }

        // single-selection path
        mat.setTRS(transform.position, transform.rotation, transform.scale);
        mat.mul2(mat, this.bindMat);
        quat.setFromMat4(mat);

        const t = mat.getTranslation();
        const r = quat;
        const s = mat.getScale();

        this.splat.moveWorld(t, r, s);
        this.top.newt.set(
            this.splat.entity.getLocalPosition(),
            this.splat.entity.getLocalRotation(),
            this.splat.entity.getLocalScale()
        );
        this.pop.newt.copy(transform);
    }

    end() {
        if (this.isMulti) {
            const ops: (EntityTransformOp | PlacePivotOp)[] = [];
            this.targets.forEach((t) => {
                if (!t.op.oldt.equals(t.op.newt)) {
                    ops.push(t.op);
                }
            });
            if (ops.length > 0) {
                ops.push(this.pop);
                this.events.fire('edit.add', new MultiOp(ops));
            }
            this.targets = [];
            this.pop = null;
            return;
        }

        // single-selection path
        const { oldt, newt } = this.top;
        if (!oldt.equals(newt)) {
            this.events.fire('edit.add', new MultiOp([this.top, this.pop]));
        }
        this.top = null;
        this.pop = null;
    }
}

export { EntityTransformHandler };
